/**
 * useColorMode Hook
 *
 * Provides access to the current color mode and functions to change it.
 * This is the primary way components interact with dark/light mode.
 *
 * ## Usage
 *
 * ```tsx
 * import { useColorMode } from "@shared/hooks/useColorMode";
 *
 * function MyComponent() {
 *   const { colorMode, toggleColorMode, isForced } = useColorMode();
 *
 *   return (
 *     <button onClick={toggleColorMode} disabled={isForced}>
 *       Current: {colorMode}
 *     </button>
 *   );
 * }
 * ```
 *
 * ## Return Values
 *
 * - `colorMode` - Current mode: "light" | "dark"
 * - `setColorMode` - Set specific mode: setColorMode("dark")
 * - `toggleColorMode` - Toggle between light/dark
 * - `isForced` - True if mode is forced (user can't toggle)
 *
 * ## SSR Note
 *
 * On first render (SSR), `colorMode` may be undefined until hydration.
 * Use optional chaining or default values when rendering mode-dependent UI.
 *
 * ## Related Files
 *
 * - `ColorModeProvider.tsx` - Provider that must wrap the app
 * - `ColorModeToggle.web.tsx` - Ready-made toggle button component
 */
import { useTheme } from "next-themes";

export function useColorMode() {
  const { theme, setTheme, forcedTheme } = useTheme();

  return {
    /** Current color mode: "light" | "dark" */
    colorMode: theme as "light" | "dark",
    /** Set a specific color mode */
    setColorMode: setTheme,
    /** Toggle between light and dark mode */
    toggleColorMode: () => setTheme(theme === "light" ? "dark" : "light"),
    /** True if color mode is forced and cannot be changed by user */
    isForced: !!forcedTheme,
  };
}
